/* Vue « Carte mentale » de l'Annuaire — mindmap horizontal éditable (Mapify-like).
 *
 * - Racine → branches secteur → entités → sous-entités (hiérarchie parent_id)
 * - Replier / déplier chaque branche (⊖ / compteur), tout plier / tout déplier
 * - Toolbar sur nœud sélectionné : Fiche · Renommer · + Enfant · Déplacer · Supprimer
 * - Glisser-déposer un nœud SUR un autre = rattachement (reparent)
 * - Export PNG de la carte telle qu'affichée
 */
import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import {
  ReactFlow, ReactFlowProvider, Background, Controls, Handle, Position,
  NodeToolbar, Panel, applyNodeChanges, useReactFlow,
  getNodesBounds, getViewportForBounds,
} from "@xyflow/react";
import "@xyflow/react/dist/style.css";
import { toPng } from "html-to-image";
import { api } from "@/lib/api";
import { notify, fmtError } from "@/lib/notify";
import {
  FileText, Pencil, Plus, Move, Trash2, Download,
  ChevronsDownUp, ChevronsUpDown, X, Search, Loader2, CornerUpLeft,
} from "lucide-react";

const PALETTE = [
  "#E2557B", "#7F77DD", "#378ADD", "#1D9E75", "#EF9F27",
  "#38BDF8", "#A78BFA", "#F472B6", "#34D399", "#E24B4A",
];

const ROW_H = 34;
const COL_W = 270;

/* ===== Hiérarchie ===== */

function buildHierarchy(tree, groupMode = "typologie") {
  if (groupMode === "typologie") {
    // À plat : chaque entité apparaît une seule fois sous sa typologie (sector)
    const flat = [];
    const collect = (list) => {
      for (const e of list || []) { flat.push(e); collect(e.children); }
    };
    collect(tree);
    const byTypo = new Map();
    for (const e of flat) {
      const typo = (e.sector || "").trim() || "Sans typologie";
      if (!byTypo.has(typo)) byTypo.set(typo, []);
      byTypo.get(typo).push(e);
    }
    const typos = [...byTypo.entries()].sort((a, b) => b[1].length - a[1].length);
    return {
      id: "root",
      label: "Annuaire",
      entityId: null,
      children: typos.map(([typo, items]) => ({
        id: `sector-${typo}`,
        label: typo,
        sector: typo,
        entityId: null,
        count: items.length,
        children: items
          .slice()
          .sort((a, b) => (a.name || "").localeCompare(b.name || ""))
          .map((e) => ({ id: e.id, label: e.name, entityId: e.id, dup: (e.tags || []).includes("doublon_probable"), children: [] })),
      })),
    };
  }
  // Mode hiérarchie : secteurs → racines → sous-entités (parent_id)
  const bySector = new Map();
  for (const root of tree) {
    const sector = (root.sector || "").trim() || "Sans secteur";
    if (!bySector.has(sector)) bySector.set(sector, []);
    bySector.get(sector).push(root);
  }
  const sectors = [...bySector.entries()].sort((a, b) => b[1].length - a[1].length);
  return {
    id: "root",
    label: "Annuaire",
    entityId: null,
    children: sectors.map(([sector, roots]) => ({
      id: `sector-${sector}`,
      label: sector,
      sector,
      entityId: null,
      count: roots.reduce((acc, r) => acc + 1 + countDesc(r), 0),
      children: roots.map(toNode),
    })),
  };
}

function countDesc(e) {
  return (e.children || []).reduce((acc, c) => acc + 1 + countDesc(c), 0);
}

function toNode(e) {
  return { id: e.id, label: e.name, entityId: e.id, dup: (e.tags || []).includes("doublon_probable"), children: (e.children || []).map(toNode) };
}

function flattenEntities(tree, out = []) {
  for (const e of tree || []) {
    out.push({ id: e.id, name: e.name, type: e.type });
    flattenEntities(e.children, out);
  }
  return out;
}

/* ===== Nœud custom ===== */

function MindNode({ id, data, selected }) {
  const { label, color, level, count, entityId, hasChildren, childCount, collapsed, renaming } = data;
  const fontSize = level === 0 ? 15 : level === 1 ? 12 : 11;
  const inputRef = useRef(null);
  useEffect(() => { if (renaming && inputRef.current) { inputRef.current.focus(); inputRef.current.select(); } }, [renaming]);

  return (
    <div
      style={{
        padding: "1px 6px 3px",
        borderBottom: `2px solid ${color}`,
        color: selected ? "#fff" : "#e4e4e7",
        fontSize,
        fontWeight: level <= 1 ? 600 : 400,
        whiteSpace: "nowrap",
        background: selected ? `${color}33` : "transparent",
        borderRadius: 4,
        cursor: "pointer",
        display: "flex",
        alignItems: "center",
        gap: 5,
      }}
      data-testid={`mindmap-node-${entityId || id}`}
    >
      <Handle type="target" position={Position.Left} style={{ opacity: 0, pointerEvents: "none" }} />
      {renaming ? (
        <input
          ref={inputRef}
          defaultValue={label}
          className="nodrag bg-black/60 border border-white/20 rounded px-1 text-zinc-100 outline-none"
          style={{ fontSize, minWidth: 140 }}
          onKeyDown={(e) => {
            if (e.key === "Enter") data.onRenameCommit(entityId, e.target.value);
            if (e.key === "Escape") data.onRenameCommit(null, null);
          }}
          onBlur={(e) => data.onRenameCommit(entityId, e.target.value)}
          data-testid="mindmap-rename-input"
        />
      ) : (
        <span>
          {data.dup && (
            <span
              title="Doublon probable — vérifiez et supprimez si nécessaire"
              style={{ display: "inline-block", width: 7, height: 7, borderRadius: 4, background: "#EF9F27", marginRight: 5 }}
              data-testid="mindmap-dup-flag"
            />
          )}
          {label}
        </span>
      )}
      {count != null && <span style={{ opacity: 0.5, fontSize: 10 }}>({count})</span>}
      {hasChildren && (
        <button
          type="button"
          className="nodrag"
          onClick={(e) => { e.stopPropagation(); data.onToggleCollapse(id); }}
          style={{
            width: 16, height: 16, borderRadius: 8, border: `1px solid ${color}`,
            color, fontSize: 9, lineHeight: "14px", background: "#0b0d12",
            display: "inline-flex", alignItems: "center", justifyContent: "center",
            fontWeight: 600, flexShrink: 0,
          }}
          title={collapsed ? `Déplier (${childCount})` : "Replier la branche"}
          data-testid={`mindmap-collapse-${entityId || id}`}
        >
          {collapsed ? childCount : "−"}
        </button>
      )}
      <NodeToolbar isVisible={selected && (!!entityId || !!data.sector)} position={Position.Bottom} offset={8}>
        <div className="flex gap-1 bg-[#14161c] border border-white/10 rounded-md p-1 shadow-xl">
          {entityId && (
            <>
              <ToolbarBtn icon={FileText} label="Fiche" onClick={() => data.onOpenDetail(entityId)} testid="mindmap-tb-detail" />
              <ToolbarBtn icon={Pencil} label="Renommer" onClick={() => data.onStartRename(entityId)} testid="mindmap-tb-rename" />
              <ToolbarBtn icon={Plus} label="Enfant" onClick={() => data.onAddChild({ parentId: entityId, label })} testid="mindmap-tb-add" />
              <ToolbarBtn icon={Move} label="Déplacer" onClick={() => data.onMove({ entityId, label })} testid="mindmap-tb-move" />
              <ToolbarBtn icon={Trash2} label="Supprimer" danger onClick={() => data.onDelete({ entityId, label, childCount })} testid="mindmap-tb-delete" />
            </>
          )}
          {!entityId && data.sector && (
            <ToolbarBtn icon={Plus} label="Nouvelle entité" onClick={() => data.onAddChild({ sector: data.sector, label })} testid="mindmap-tb-add-sector" />
          )}
        </div>
      </NodeToolbar>
      <Handle type="source" position={Position.Right} style={{ opacity: 0, pointerEvents: "none" }} />
    </div>
  );
}

function ToolbarBtn({ icon: Icon, label, onClick, danger, testid }) {
  return (
    <button
      type="button"
      onClick={(e) => { e.stopPropagation(); onClick(); }}
      className={`text-[10px] px-1.5 py-1 rounded inline-flex items-center gap-1 transition-colors ${
        danger ? "text-[#E24B4A] hover:bg-[#E24B4A]/15" : "text-zinc-300 hover:bg-white/10"
      }`}
      data-testid={testid}
    >
      <Icon className="w-3 h-3" /> {label}
    </button>
  );
}

const NODE_TYPES = { mind: MindNode };

/* ===== Composant principal ===== */

function AnnuaireMindmapInner({ tree, onSelect, onChanged }) {
  const [nodes, setNodes] = useState([]);
  const [edges, setEdges] = useState([]);
  const [collapsed, setCollapsed] = useState(() => new Set());
  const [groupMode, setGroupMode] = useState("typologie"); // typologie | hierarchie
  const [renamingId, setRenamingId] = useState(null);
  const [dialog, setDialog] = useState(null); // {type:'add'|'move'|'delete', ...}
  const [exporting, setExporting] = useState(false);
  const { getNodes, getIntersectingNodes, setViewport } = useReactFlow();
  const viewportInit = useRef(false);

  const allEntities = useMemo(() => flattenEntities(tree), [tree]);

  /* ----- Actions ----- */

  const onToggleCollapse = useCallback((id) => {
    setCollapsed((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id); else next.add(id);
      return next;
    });
  }, []);

  const onStartRename = useCallback((entityId) => setRenamingId(entityId), []);

  const onRenameCommit = useCallback(async (entityId, value) => {
    setRenamingId(null);
    if (!entityId || !value || !value.trim()) return;
    try {
      await api.patch(`/workspace/annuaire/entities/${entityId}`, { name: value.trim() });
      notify.success("Entité renommée.");
      onChanged?.();
    } catch (e) { notify.error(fmtError(e)); }
  }, [onChanged]);

  const onAddChild = useCallback((ctx) => setDialog({ type: "add", ...ctx }), []);
  const onMove = useCallback((ctx) => setDialog({ type: "move", ...ctx }), []);
  const onDelete = useCallback((ctx) => setDialog({ type: "delete", ...ctx }), []);
  const onOpenDetail = useCallback((entityId) => onSelect?.(entityId), [onSelect]);

  /* ----- Layout ----- */

  const relayout = useCallback(() => {
    const root = buildHierarchy(tree || [], groupMode);
    const ns = [];
    const es = [];
    let cursor = 0;

    const leafCount = (n) => {
      if (collapsed.has(n.id) || !n.children || n.children.length === 0) return 1;
      return n.children.reduce((acc, c) => acc + leafCount(c), 0);
    };

    const walk = (node, depth, color, parentId) => {
      const leaves = leafCount(node);
      const y = (cursor + leaves / 2) * ROW_H;
      const isCollapsed = collapsed.has(node.id);
      const hasChildren = (node.children || []).length > 0;
      ns.push({
        id: node.id,
        type: "mind",
        position: { x: depth * COL_W, y },
        data: {
          label: node.label,
          color,
          level: depth,
          count: depth === 1 ? node.count : undefined,
          entityId: node.entityId,
          sector: node.sector,
          dup: node.dup,
          hasChildren,
          childCount: countDesc(node) || (node.children || []).length,
          collapsed: isCollapsed,
          renaming: node.entityId != null && node.entityId === renamingId,
          onToggleCollapse, onStartRename, onRenameCommit,
          onAddChild, onMove, onDelete, onOpenDetail,
        },
        sourcePosition: Position.Right,
        targetPosition: Position.Left,
        draggable: node.entityId != null,
      });
      if (parentId != null) {
        es.push({
          id: `e-${node.id}`,
          source: parentId,
          target: node.id,
          type: "bezier",
          style: { stroke: color, strokeWidth: depth <= 1 ? 1.8 : 1.2, opacity: 0.75 },
        });
      }
      if (hasChildren && !isCollapsed) {
        node.children.forEach((c, i) => {
          const childColor = depth === 0 ? PALETTE[i % PALETTE.length] : color;
          walk(c, depth + 1, childColor, node.id);
        });
      } else {
        cursor += 1;
      }
    };
    walk(root, 0, "#71717a", null);
    setNodes(ns);
    setEdges(es);
    if (!viewportInit.current && ns.length) {
      viewportInit.current = true;
      const total = leafCount(root);
      setViewport({ x: 40, y: 320 - (total / 2) * ROW_H * 0.65, zoom: 0.65 });
    }
  }, [tree, collapsed, renamingId, onToggleCollapse, onStartRename, onRenameCommit, onAddChild, onMove, onDelete, onOpenDetail, setViewport]);

  useEffect(() => { relayout(); }, [relayout]);

  const onNodesChange = useCallback((changes) => {
    setNodes((nds) => applyNodeChanges(changes, nds));
  }, []);

  /* ----- Drag & drop = reparent ----- */

  const onNodeDragStop = useCallback(async (_, node) => {
    if (!node.data?.entityId) { relayout(); return; }
    const hits = getIntersectingNodes(node).filter((n) => n.id !== node.id);
    const target = hits.find((n) => n.data?.entityId) || hits.find((n) => n.data?.sector) || hits.find((n) => n.id === "root");
    if (!target) { relayout(); return; }
    try {
      if (target.data?.entityId) {
        await api.patch(`/workspace/annuaire/entities/${node.data.entityId}`, { parent_id: target.data.entityId });
        notify.success(`« ${node.data.label} » rattaché à « ${target.data.label} ».`);
      } else if (target.data?.sector) {
        const sectorValue = ["Sans secteur", "Sans typologie"].includes(target.data.sector) ? "" : target.data.sector;
        const patch = groupMode === "typologie" ? { sector: sectorValue } : { parent_id: "", sector: sectorValue };
        await api.patch(`/workspace/annuaire/entities/${node.data.entityId}`, patch);
        notify.success(`« ${node.data.label} » déplacé dans « ${target.data.label} ».`);
      } else {
        await api.patch(`/workspace/annuaire/entities/${node.data.entityId}`, { parent_id: "" });
        notify.success(`« ${node.data.label} » détaché (racine).`);
      }
      onChanged?.();
    } catch (e) { notify.error(fmtError(e)); relayout(); }
  }, [getIntersectingNodes, onChanged, relayout, groupMode]);

  /* ----- Export PNG ----- */

  const exportPng = useCallback(async () => {
    setExporting(true);
    try {
      const bounds = getNodesBounds(getNodes());
      const w = Math.min(Math.max(bounds.width + 120, 800), 4000);
      const h = Math.min(Math.max(bounds.height + 120, 600), 6000);
      const vp = getViewportForBounds(bounds, w, h, 0.03, 2, 0.05);
      const el = document.querySelector(".react-flow__viewport");
      const dataUrl = await toPng(el, {
        backgroundColor: "#0b0d12",
        width: w,
        height: h,
        style: {
          width: `${w}px`,
          height: `${h}px`,
          transform: `translate(${vp.x}px, ${vp.y}px) scale(${vp.zoom})`,
        },
        filter: (n) => !(n?.classList?.contains?.("react-flow__controls") || n?.classList?.contains?.("react-flow__panel")),
      });
      const a = document.createElement("a");
      a.href = dataUrl;
      a.download = "annuaire-carte-mentale.png";
      a.click();
      notify.success("Carte exportée en PNG.");
    } catch (e) {
      notify.error("Export PNG impossible : " + (e?.message || e));
    }
    setExporting(false);
  }, [getNodes]);

  /* ----- Tout plier / déplier ----- */

  const collapseAll = useCallback(() => {
    const ids = new Set();
    const mark = (list) => {
      for (const e of list || []) {
        if ((e.children || []).length) { ids.add(e.id); mark(e.children); }
      }
    };
    mark(tree);
    // Replie aussi les branches secteur (nœuds virtuels) → carte réduite à Racine → secteurs
    for (const root of tree || []) {
      ids.add(`sector-${(root.sector || "").trim() || "Sans secteur"}`);
    }
    setCollapsed(ids);
  }, [tree]);

  const expandAll = useCallback(() => setCollapsed(new Set()), []);

  /* ----- Mutations des dialogues ----- */

  const doAdd = useCallback(async (name) => {
    try {
      await api.post("/workspace/annuaire/entities", {
        name,
        type: "societe",
        status: "actif",
        parent_id: dialog?.parentId || null,
        sector: dialog?.sector && dialog.sector !== "Sans secteur" ? dialog.sector : null,
      });
      notify.success(`« ${name} » créé.`);
      setDialog(null);
      onChanged?.();
    } catch (e) { notify.error(fmtError(e)); }
  }, [dialog, onChanged]);

  const doMove = useCallback(async (targetId) => {
    try {
      await api.patch(`/workspace/annuaire/entities/${dialog.entityId}`, { parent_id: targetId || "" });
      notify.success("Entité déplacée.");
      setDialog(null);
      onChanged?.();
    } catch (e) { notify.error(fmtError(e)); }
  }, [dialog, onChanged]);

  const doDelete = useCallback(async (cascade) => {
    try {
      await api.delete(`/workspace/annuaire/entities/${dialog.entityId}?cascade=${cascade}`);
      notify.success("Entité supprimée.");
      setDialog(null);
      onChanged?.();
    } catch (e) { notify.error(fmtError(e)); }
  }, [dialog, onChanged]);

  return (
    <div className="w-full h-full relative">
      <ReactFlow
        nodes={nodes}
        edges={edges}
        nodeTypes={NODE_TYPES}
        onNodesChange={onNodesChange}
        onNodeDragStop={onNodeDragStop}
        minZoom={0.05}
        maxZoom={2}
        proOptions={{ hideAttribution: true }}
        nodesConnectable={false}
        elementsSelectable
        data-testid="annuaire-mindmap-flow"
      >
        <Background color="#ffffff10" gap={24} />
        <Controls showInteractive={false} />
        <Panel position="top-left">
          <div className="inline-flex rounded-md border border-white/10 bg-black/50 p-0.5">
            <button
              type="button"
              onClick={() => setGroupMode("typologie")}
              className={`text-[10px] px-2 py-1 rounded transition-colors ${groupMode === "typologie" ? "bg-[#378ADD]/25 text-[#378ADD]" : "text-zinc-400 hover:text-white"}`}
              data-testid="mindmap-group-typologie"
            >
              Par typologie
            </button>
            <button
              type="button"
              onClick={() => setGroupMode("hierarchie")}
              className={`text-[10px] px-2 py-1 rounded transition-colors ${groupMode === "hierarchie" ? "bg-[#378ADD]/25 text-[#378ADD]" : "text-zinc-400 hover:text-white"}`}
              data-testid="mindmap-group-hierarchie"
            >
              Par hiérarchie
            </button>
          </div>
        </Panel>
        <Panel position="top-right">
          <div className="flex gap-1.5">
            <button type="button" onClick={collapseAll} className="btn-secondary text-[10px] px-2 py-1" data-testid="mindmap-collapse-all">
              <ChevronsDownUp className="w-3 h-3" /> Tout replier
            </button>
            <button type="button" onClick={expandAll} className="btn-secondary text-[10px] px-2 py-1" data-testid="mindmap-expand-all">
              <ChevronsUpDown className="w-3 h-3" /> Tout déplier
            </button>
            <button type="button" onClick={exportPng} disabled={exporting} className="btn-primary text-[10px] px-2 py-1" data-testid="mindmap-export-png">
              {exporting ? <Loader2 className="w-3 h-3 animate-spin" /> : <Download className="w-3 h-3" />} Exporter PNG
            </button>
          </div>
        </Panel>
        <Panel position="bottom-right">
          <div className="text-[10px] text-zinc-500 bg-black/50 rounded px-2 py-1 max-w-[300px]">
            Astuce : glissez un nœud sur un autre pour le rattacher · clic = actions · ⊖ pour replier une branche
          </div>
        </Panel>
      </ReactFlow>

      {dialog?.type === "add" && (
        <AddDialog context={dialog} onCancel={() => setDialog(null)} onConfirm={doAdd} />
      )}
      {dialog?.type === "move" && (
        <MoveDialog context={dialog} entities={allEntities} onCancel={() => setDialog(null)} onConfirm={doMove} />
      )}
      {dialog?.type === "delete" && (
        <DeleteDialog context={dialog} onCancel={() => setDialog(null)} onConfirm={doDelete} />
      )}
    </div>
  );
}

/* ===== Dialogues ===== */

function DialogShell({ title, onCancel, children, testid }) {
  return (
    <div className="absolute inset-0 z-20 flex items-center justify-center bg-black/60" onClick={onCancel} data-testid={testid}>
      <div className="bg-[#14161c] border border-white/10 rounded-lg p-4 w-[380px] max-w-[90%] shadow-2xl" onClick={(e) => e.stopPropagation()}>
        <div className="flex items-center justify-between mb-3">
          <div className="text-sm font-semibold">{title}</div>
          <button type="button" onClick={onCancel} className="text-zinc-500 hover:text-white"><X className="w-4 h-4" /></button>
        </div>
        {children}
      </div>
    </div>
  );
}

function AddDialog({ context, onCancel, onConfirm }) {
  const [name, setName] = useState("");
  return (
    <DialogShell title={`Nouvelle entité sous « ${context.label} »`} onCancel={onCancel} testid="mindmap-add-dialog">
      <input
        autoFocus
        value={name}
        onChange={(e) => setName(e.target.value)}
        onKeyDown={(e) => { if (e.key === "Enter" && name.trim()) onConfirm(name.trim()); }}
        placeholder="Nom de l'entité…"
        className="w-full bg-black/40 border border-white/10 rounded px-2.5 py-1.5 text-sm outline-none focus:border-[#378ADD]/50"
        data-testid="mindmap-add-name-input"
      />
      <div className="flex justify-end gap-2 mt-3">
        <button type="button" onClick={onCancel} className="btn-secondary text-xs">Annuler</button>
        <button type="button" disabled={!name.trim()} onClick={() => onConfirm(name.trim())} className="btn-primary text-xs" data-testid="mindmap-add-confirm">
          <Plus className="w-3 h-3" /> Créer
        </button>
      </div>
    </DialogShell>
  );
}

function MoveDialog({ context, entities, onCancel, onConfirm }) {
  const [q, setQ] = useState("");
  const filtered = useMemo(() => {
    const qq = q.trim().toLowerCase();
    return entities
      .filter((e) => e.id !== context.entityId)
      .filter((e) => !qq || e.name.toLowerCase().includes(qq))
      .slice(0, 40);
  }, [entities, q, context.entityId]);
  return (
    <DialogShell title={`Déplacer « ${context.label} » vers…`} onCancel={onCancel} testid="mindmap-move-dialog">
      <div className="relative">
        <Search className="w-3.5 h-3.5 absolute left-2.5 top-2.5 text-zinc-500" />
        <input
          autoFocus
          value={q}
          onChange={(e) => setQ(e.target.value)}
          placeholder="Rechercher un parent…"
          className="w-full bg-black/40 border border-white/10 rounded pl-8 pr-2.5 py-1.5 text-sm outline-none focus:border-[#378ADD]/50"
          data-testid="mindmap-move-search"
        />
      </div>
      <div className="mt-2 max-h-[260px] overflow-y-auto space-y-0.5">
        <button
          type="button"
          onClick={() => onConfirm("")}
          className="w-full text-left px-2.5 py-1.5 rounded text-xs text-zinc-300 hover:bg-white/10 inline-flex items-center gap-1.5"
          data-testid="mindmap-move-root"
        >
          <CornerUpLeft className="w-3 h-3" /> Racine (aucun parent)
        </button>
        {filtered.map((e) => (
          <button
            key={e.id}
            type="button"
            onClick={() => onConfirm(e.id)}
            className="w-full text-left px-2.5 py-1.5 rounded text-xs text-zinc-300 hover:bg-white/10 truncate"
            data-testid={`mindmap-move-target-${e.id}`}
          >
            {e.name} <span className="text-zinc-600">· {e.type}</span>
          </button>
        ))}
      </div>
    </DialogShell>
  );
}

function DeleteDialog({ context, onCancel, onConfirm }) {
  const hasChildren = (context.childCount || 0) > 0;
  return (
    <DialogShell title={`Supprimer « ${context.label} » ?`} onCancel={onCancel} testid="mindmap-delete-dialog">
      <p className="text-xs text-zinc-400">
        {hasChildren
          ? `Cette entité porte ${context.childCount} sous-entité(s). La suppression en cascade les supprimera aussi.`
          : "Cette action est définitive."}
      </p>
      <div className="flex justify-end gap-2 mt-3">
        <button type="button" onClick={onCancel} className="btn-secondary text-xs">Annuler</button>
        <button
          type="button"
          onClick={() => onConfirm(hasChildren)}
          className="text-xs px-3 py-1.5 rounded bg-[#E24B4A]/20 text-[#E24B4A] border border-[#E24B4A]/30 hover:bg-[#E24B4A]/30 inline-flex items-center gap-1.5"
          data-testid="mindmap-delete-confirm"
        >
          <Trash2 className="w-3 h-3" /> {hasChildren ? `Supprimer (+ ${context.childCount} sous-entités)` : "Supprimer"}
        </button>
      </div>
    </DialogShell>
  );
}

export default function AnnuaireMindmap({ tree, selectedId, onSelect, onChanged }) {
  return (
    <ReactFlowProvider>
      <AnnuaireMindmapInner tree={tree} selectedId={selectedId} onSelect={onSelect} onChanged={onChanged} />
    </ReactFlowProvider>
  );
}

